import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { Helmet } from 'react-helmet';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import { RouteComponentProps } from '@reach/router';
import {
  getDomainAndPort,
  getProtocolDomainAndPort,
  isOutPatientDomain,
  outpatientContent,
} from 'api/Utils';
import { RootState } from 'store/store';
import { CircleContainer, FormContainer } from './styles';
import messages from './Messages';
import ChangeName from './ChangeName';
import LogoLoading from 'components/LogoLoading';
import { display } from 'components/ErrorView/ErrorDialog';
import ChangePhoneNumber from './ChangePhoneNumber';
import notify from 'components/Notify';
import ChangePassword from './ChangePassword';
import MyLogViewPanel from './MyLogViewPanel';
import { responseTitleStyle } from '../../../components/FileManager/ItemsViewer/Survey/SurveyResponseViewer/styles';
import Collapsible from '../../../components/Collapsible';
import Toggle from '../../../components/Toggle/Toggle';
import { getCookie, setCookie } from '../../../utils/happyFunctions';
import { appDispatch } from 'store/store';
import {
  enterWithSingleAccessCode,
  enterWithSingleAccessCodeFn,
  IThisUserState,
  requestChangePhoneNumber,
  requestChangePhoneNumberFn,
  validatePhoneNumber,
  validatePhoneNumberFn,
} from 'store/thisUser/thisUserSlice';
import { IUser } from 'store/users/usersType';
import {
  requireUser,
  requireUserFn,
  updateUser,
  updateUserFn,
} from 'store/users/usersSlice';
import i18n from '../../../i18n';
import LocalIcon from '../../../ui/LocalIcon';
import { exactWhite } from 'utils/style/colors';
import Button from 'components/Button';

type McpClient = 'cursor' | 'claudeCode' | 'claudeDesktop';

const buildMcpConfig = (client: McpClient, accessToken: string) => {
  const mcpUrl = `${getProtocolDomainAndPort(getDomainAndPort())}/man/rest/mcp`;
  const authorization = `Bearer ${accessToken}`;

  if (client === 'claudeDesktop') {
    return {
      mcpServers: {
        hospitech: {
          command: 'npx',
          args: [
            '-y',
            'mcp-remote',
            mcpUrl,
            '--header',
            'Authorization:${AUTH_HEADER}',
          ],
          env: {
            AUTH_HEADER: authorization,
          },
        },
      },
    };
  }

  return {
    mcpServers: {
      hospitech: {
        ...(client === 'claudeCode' ? { type: 'http' } : {}),
        url: mcpUrl,
        headers: {
          Authorization: authorization,
        },
      },
    },
  };
};

interface IStateToProps {
  user: IUser;
  userId: number;
  thisUser: IThisUserState;
}

interface IDispatchToProps {
  getUser: (userId: number) => ReturnType<typeof requireUserFn>;
  updateUserInfo: (user: IUser) => ReturnType<typeof updateUserFn>;
  validateEnteredNumber: (
    secret: string,
    token: string,
    phoneNumber: string
  ) => ReturnType<typeof validatePhoneNumberFn>;
  changePhoneNumber: (
    phoneNumber: string
  ) => ReturnType<typeof requestChangePhoneNumberFn>;
  loginWithPhoneNumber: (
    loginToken: string,
    codeNumber: string,
    staySignedIn: boolean
  ) => ReturnType<typeof enterWithSingleAccessCodeFn>;
}

type IComponentProps = IStateToProps & IDispatchToProps & RouteComponentProps;

interface IComponentState {
  loading: boolean;
  random: number;
}

class ProfileContainer extends React.PureComponent<
  IComponentProps,
  IComponentState
> {
  state: IComponentState = { loading: false, random: 1 };

  componentDidMount() {
    const { user, userId, getUser } = this.props;
    if (!user) {
      getUser(userId).catch((err: any) => {
        display(err);
      });
    }
  }

  handleNameSubmit = (updatedName: string) => {
    const { user, updateUserInfo } = this.props;
    // request when user's name has changed
    if (updatedName !== user.name) {
      this.setState({ loading: true });
      const updatedUser: IUser = cloneDeep(user);
      updatedUser.name = updatedName;
      updateUserInfo(updatedUser).then(
        () => {
          this.setState({ loading: false });
          notify(messages.userUpdatedSuccessfully);
          if (isOutPatientDomain()) {
            setTimeout(() => (window.location.href = outpatientContent), 1000);
          }
        },
        (err) => {
          this.setState({ loading: false });
          display(err);
        }
      );
    } else {
      notify(messages.profileNameNotChanged, 'warning');
    }
  };

  onChangeUseBeta = (e: React.ChangeEvent<HTMLInputElement>) => {
    const result = e.target.checked;
    if (confirm(messages.reloadOnBeta)) {
      setCookie('useBeta', result.toString(), 14);
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } else this.setState({});
  };

  copyMcpConfig = (client: McpClient) => {
    const accessToken = this.props.thisUser.access_token;
    if (!accessToken) {
      notify(
        i18n.t('mcpConfigCopyFailed', { ns: 'profile' }) as string,
        'error'
      );
      return;
    }

    const config = buildMcpConfig(client, accessToken);
    navigator.clipboard.writeText(JSON.stringify(config, null, 2)).then(
      () => {
        notify(i18n.t('mcpConfigCopied', { ns: 'profile' }) as string);
      },
      () => {
        notify(
          i18n.t('mcpConfigCopyFailed', { ns: 'profile' }) as string,
          'error'
        );
      }
    );
  };

  render() {
    const {
      user,
      thisUser,
      changePhoneNumber,
      loginWithPhoneNumber,
      validateEnteredNumber,
    } = this.props;
    const usingBeta = getCookie('useBeta') === 'true';
    const { random } = this.state;
    return user ? (
      <div
        className="w-full"
        style={{
          background: exactWhite,
          overflow: 'auto',
          minHeight: '100%',
        }}
      >
        <Helmet>
          <title>{i18n.t('profile', { ns: 'profile' }) as string}</title>
          <meta
            name="description"
            content={i18n.t('profile', { ns: 'profile' }) as string}
          />
        </Helmet>
        <div
          className="row"
          style={{ width: 'calc(100% - 1rem)', maxWidth: '500px' }}
        >
          <div className="flex align-center justify-end gap-2 mt-1">
            {/*if back is needed add logic of it*/}
            {/*<CircleContainer>*/}
            {/*  <LocalIcon*/}
            {/*    iconName={'arrow-top.svg'}*/}
            {/*    onClick={() => {*/}
            {/*      console.log('');*/}
            {/*    }}*/}
            {/*    isLink={true}*/}
            {/*  />*/}
            {/*</CircleContainer>*/}
            <CircleContainer>
              <LocalIcon
                iconName={'back-square.svg'}
                onClick={() => {
                  history.back();
                }}
                isLink={true}
              />
            </CircleContainer>
          </div>
          <FormContainer>
            <Collapsible
              headerBackgroundColor="var(--tertiary-gray)"
              iconName="user"
              customHeaderStyle={responseTitleStyle}
              defaultOpen={true}
              customBodyStyle={{ maxHeight: 'unset !important' }}
              header={
                <React.Fragment>
                  <span style={responseTitleStyle}>
                    {i18n.t('basicInfo', { ns: 'profile' }) as string}
                  </span>
                </React.Fragment>
              }
            >
              <>
                <ChangeName
                  loading={this.state.loading}
                  userName={user.name}
                  onNameSubmit={this.handleNameSubmit}
                />
                <ChangePhoneNumber
                  phoneNumber={user.phoneNumber!}
                  onSubmitPhoneNumber={changePhoneNumber}
                  onSubmitVerifySecret={validateEnteredNumber}
                  loginWithPhoneNumber={loginWithPhoneNumber}
                  issueDate={thisUser.issueDate!}
                />
              </>
            </Collapsible>
            <Collapsible
              headerBackgroundColor="var(--tertiary-gray)"
              iconName="log-in"
              customHeaderStyle={responseTitleStyle}
              defaultOpen={window.location.hash === '#password'}
              nested={true}
              checkHeightAgain={random}
              header={
                <React.Fragment>
                  <span style={responseTitleStyle}>
                    {
                      i18n.t('authentication', {
                        ns: 'profile',
                      }) as string
                    }
                  </span>
                </React.Fragment>
              }
            >
              <ChangePassword
                changedHeight={() => this.setState({ random: Math.random() })}
              />
            </Collapsible>
            <Collapsible
              headerBackgroundColor="var(--tertiary-gray)"
              iconName="clock"
              customHeaderStyle={responseTitleStyle}
              defaultOpen={false}
              nested={true}
              header={
                <React.Fragment>
                  <span style={responseTitleStyle}>
                    {
                      i18n.t('loginHistory', {
                        ns: 'profile',
                      }) as string
                    }
                  </span>
                </React.Fragment>
              }
            >
              <MyLogViewPanel />
            </Collapsible>
            <Collapsible
              headerBackgroundColor="var(--tertiary-gray)"
              iconName="code"
              customHeaderStyle={responseTitleStyle}
              defaultOpen={false}
              header={
                <React.Fragment>
                  <span style={responseTitleStyle}>
                    {i18n.t('advancedSettings', { ns: 'profile' }) as string}
                  </span>
                </React.Fragment>
              }
            >
              <div>
                <Toggle
                  wrapperStyle={{}}
                  label={i18n.t('useBeta', { ns: 'profile' }) as string}
                  name="batchAdd"
                  title={i18n.t('useBeta', { ns: 'profile' }) as string}
                  onChange={this.onChangeUseBeta}
                  checked={usingBeta}
                />
                <div className="flex flex-col gap-2 mt-4">
                  <span style={responseTitleStyle}>
                    {i18n.t('mcpIntegration', { ns: 'profile' }) as string}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="small"
                      type="button"
                      onClick={() => this.copyMcpConfig('cursor')}
                    >
                      {i18n.t('copyCursorMcp', { ns: 'profile' }) as string}
                    </Button>
                    <Button
                      size="small"
                      type="button"
                      onClick={() => this.copyMcpConfig('claudeCode')}
                    >
                      {
                        i18n.t('copyClaudeCodeMcp', {
                          ns: 'profile',
                        }) as string
                      }
                    </Button>
                    <Button
                      size="small"
                      type="button"
                      onClick={() => this.copyMcpConfig('claudeDesktop')}
                    >
                      {
                        i18n.t('copyClaudeDesktopMcp', {
                          ns: 'profile',
                        }) as string
                      }
                    </Button>
                  </div>
                </div>
              </div>
            </Collapsible>
          </FormContainer>
        </div>
      </div>
    ) : (
      <LogoLoading />
    );
  }
}

const mapStateToProps: MapStateToProps<
  IStateToProps,
  RouteComponentProps,
  RootState
> = (state: RootState) => {
  const userId = Number(state.thisUser.userId);
  const user = state.users.map[userId];
  const thisUser = state.thisUser;

  return { user, userId, thisUser };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  RouteComponentProps
> = () => {
  return {
    getUser: (userId: number) => appDispatch(requireUser({ userId })),
    updateUserInfo: (user: IUser) => appDispatch(updateUser({ user })),
    validateEnteredNumber: (
      secret: string,
      token: string,
      phoneNumber: string
    ) => appDispatch(validatePhoneNumber({ secret, token, phoneNumber })),
    changePhoneNumber: (phoneNumber: string) =>
      appDispatch(requestChangePhoneNumber({ phoneNumber })),
    loginWithPhoneNumber: (
      loginToken: string,
      codeNumber: string,
      staySignedIn: boolean
    ) =>
      appDispatch(
        enterWithSingleAccessCode({ loginToken, codeNumber, staySignedIn })
      ),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfileContainer);
